import { useState, useEffect, useCallback } from 'react';
import { Sun, Battery, BatteryCharging, BatteryLow, Target, ArrowRight, Check, X, ShieldAlert, Flame } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { SigninAnomalyChip } from '../security/SigninAnomalyChip';
import { countUnrecognisedDevices } from '../security/unrecognisedDevices';
import { logger } from '../../utils/logger';

/**
 * Daybreak ritual — a 2-minute guided morning flow.
 * Fires once per calendar day on app load, unless the user has dismissed or completed it.
 * Three steps:
 *   1. Commit to up to 3 outcomes for today
 *   2. Energy check (low / balanced / high)
 *   3. One-line primary focus, then save
 */
const ENERGY_OPTIONS = [
  { key: 'low',       label: 'Low',      icon: BatteryLow,      tone: 'var(--pd-warning)' },
  { key: 'balanced',  label: 'Balanced', icon: Battery,         tone: 'var(--pd-focus)' },
  { key: 'high',      label: 'High',     icon: BatteryCharging, tone: 'var(--pd-green)' },
];

// "Welcome back · last seen X" stays hidden when the user signed in less
// than this many ms ago — avoids the awkward "just now" right after a
// fresh login. Tune here if the welcome chip starts feeling stale.
const JUST_LOGGED_IN_THRESHOLD_MS = 5 * 60 * 1000;

export default function DaybreakModal({ open, onClose }) {
  useBodyScrollLock(open);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [outcomes, setOutcomes] = useState(['', '', '']);
  const [energy, setEnergy] = useState('balanced');
  const [focus, setFocus] = useState('');
  const [saving, setSaving] = useState(false);
  // Streak state — fetched lazily on modal open via `/api/daybreak/today`
  // so we can render the flame chip in the header. `currentStreak`
  // is the count of consecutive days ending today (or yesterday if
  // we're in the 2-hour grace window). `streakStatus` drives the
  // microcopy: `active` celebrates, `at_risk` nudges, `broken` stays
  // silent.
  const [streak, setStreak] = useState({ current: 0, longest: 0, status: 'broken' });

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setOutcomes(['', '', '']);
    setEnergy('balanced');
    setFocus('');
    // Fire-and-forget; we don't gate the modal on this — the chip
    // just won't render until it lands. Failure is silent because
    // the streak is non-essential UX.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/daybreak/today');
        if (cancelled) return;
        setStreak({
          current: data?.currentStreak ?? 0,
          longest: data?.longestStreak ?? 0,
          status: data?.streakStatus ?? 'broken',
        });
      } catch (err) {
        logger.warn('Daybreak streak fetch failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Escape closes modal (a11y: every modal must be Escape-dismissable)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // The response carries the freshly-recomputed streak — pull it
      // into local state so the "Streak +1!" celebration chip can
      // render briefly before the modal closes.
      const { data } = await api.post('/daybreak/complete', {
        outcomes: outcomes.filter((o) => o.trim()),
        energy,
        focus: focus.trim(),
      });
      setStreak({
        current: data?.currentStreak ?? 0,
        longest: data?.longestStreak ?? 0,
        status: data?.streakStatus ?? 'active',
      });
      onClose?.(true);
    } catch (err) {
      logger.error('Daybreak save failed:', err);
      setSaving(false);
    }
  }, [outcomes, energy, focus, onClose]);

  const goNext = () => setStep((s) => Math.min(s + 1, 2));
  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  // "Welcome back" line — computed once at open so we sidestep
  // "Date.now in render" purity warnings. Hidden when last sign-in is
  // < 5 min ago (avoids "just now" right after fresh login).
  const [lastSeenLabel, setLastSeenLabel] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (!user?.lastLoginAt) { setLastSeenLabel(null); return; }
    const last = new Date(user.lastLoginAt);
    if (Number.isNaN(last.getTime())) { setLastSeenLabel(null); return; }
    if (Date.now() - last.getTime() < JUST_LOGGED_IN_THRESHOLD_MS) { setLastSeenLabel(null); return; }
    try {
      setLastSeenLabel(formatDistanceToNow(last, { addSuffix: true }));
    } catch (err) {
      logger.warn('Daybreak lastSeen formatting failed:', err?.message);
      setLastSeenLabel(null);
    }
  }, [open, user?.lastLoginAt]);

  if (!open) return null;

  const canProceedStep0 = outcomes.some((o) => o.trim().length > 0);

  return (
    <div
      data-testid="daybreak-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daybreak-modal-title"
      className="fixed inset-0 z-[70] overflow-y-auto animate-fade-in"
      style={{ background: 'rgba(5,5,7,0.72)', backdropFilter: 'blur(40px) saturate(140%)', WebkitBackdropFilter: 'blur(40px) saturate(140%)' }}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-8">
      <div
        className="w-full max-w-[580px] rounded-[28px] overflow-hidden"
        style={{
          background: 'var(--pd-surface-3)',
          border: '1px solid var(--pd-border-highlight)',
          boxShadow: 'var(--pd-shadow-command), var(--pd-edge-highlight)',
          backdropFilter: 'blur(28px) saturate(160%)',
          WebkitBackdropFilter: 'blur(28px) saturate(160%)',
          animation: 'pd-modal-rise 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-3">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color: 'var(--pd-warning)' }}>
              <Sun className="w-3 h-3" /> Daybreak · 2 min
              {streak.current > 0 && (
                <StreakChip current={streak.current} status={streak.status} />
              )}
            </div>
            {lastSeenLabel && (
              <div
                data-testid="daybreak-welcome-back"
                className="text-[11px] mt-1.5"
                style={{ color: 'var(--pd-subtle)', letterSpacing: '-0.005em' }}
              >
                Welcome back · last seen {lastSeenLabel}
              </div>
            )}
            <SigninAnomalyChip
              anomaly={user?.signinAnomaly}
              lastLoginAt={user?.lastLoginAt}
              variant="chip"
              testId="daybreak-signin-anomaly"
            />
            <DaybreakSecurityNudge
              user={user}
              onReview={() => { onClose?.(false); navigate('/settings'); }}
            />
            <h2 className="text-[24px] font-medium mt-2" style={{ letterSpacing: '-0.04em', color: 'var(--pd-text)' }}>
              {step === 0 ? 'What wins today?' : step === 1 ? 'How is your energy?' : 'Your primary focus?'}
            </h2>
            <p className="text-[12px] mt-1" style={{ color: 'var(--pd-subtle)' }}>
              {step === 0 ? 'Name up to three outcomes. Just the ones that matter.' :
                step === 1 ? 'Honest input — we will calibrate your plan accordingly.' :
                'One sentence on the single thing you will not let slip today.'}
            </p>
          </div>
          <button
            data-testid="daybreak-skip"
            aria-label="Skip Daybreak for today"
            onClick={() => onClose?.(false)}
            className="pd-icon-button flex-shrink-0"
            title="Skip for today"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1.5 px-6 pb-4">
          {[0, 1, 2].map((i) => (
            <div
              key={`step-${i}`}
              className="flex-1 h-1 rounded-full transition-colors"
              style={{ background: i <= step ? 'var(--pd-focus)' : 'var(--pd-border)' }}
            />
          ))}
        </div>

        <div className="px-6 pb-6">
          {/* STEP 0: outcomes */}
          {step === 0 && (
            <div data-testid="daybreak-step-outcomes" className="space-y-2.5">
              {outcomes.map((val, idx) => (
                <div key={`outcome-row-${idx}`} className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 text-[11px] font-bold" style={{ background: val.trim() ? 'var(--pd-focus)' : 'var(--pd-panel-2)', color: val.trim() ? 'white' : 'var(--pd-subtle)' }}>
                    {idx + 1}
                  </div>
                  <input
                    data-testid={`daybreak-outcome-${idx}`}
                    value={val}
                    onChange={(e) => setOutcomes((prev) => prev.map((o, i) => i === idx ? e.target.value : o))}
                    placeholder={idx === 0 ? 'e.g., Finalize SRLP pitch deck' : idx === 1 ? 'Optional' : 'Optional'}
                    className="flex-1 px-4 py-3 rounded-xl text-[13px] font-medium outline-none transition-colors"
                    style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)', color: 'var(--pd-text)' }}
                    autoFocus={idx === 0}
                    maxLength={200}
                  />
                </div>
              ))}
            </div>
          )}

          {/* STEP 1: energy */}
          {step === 1 && (
            <div data-testid="daybreak-step-energy" className="grid grid-cols-3 gap-3">
              {ENERGY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = energy === opt.key;
                return (
                  <button
                    key={opt.key}
                    data-testid={`daybreak-energy-${opt.key}`}
                    onClick={() => setEnergy(opt.key)}
                    className="p-4 rounded-2xl text-left transition-all"
                    style={{
                      background: active ? 'var(--pd-panel-2)' : 'transparent',
                      border: `2px solid ${active ? opt.tone : 'var(--pd-border)'}`,
                      transform: active ? 'translateY(-1px)' : 'none',
                    }}
                  >
                    <Icon className="w-5 h-5 mb-2" style={{ color: opt.tone }} />
                    <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>{opt.label}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* STEP 2: focus */}
          {step === 2 && (
            <div data-testid="daybreak-step-focus">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />
                <span className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'var(--pd-subtle)' }}>One thing that must get done</span>
              </div>
              <textarea
                data-testid="daybreak-focus-input"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                placeholder={outcomes.find((o) => o.trim()) || 'The single most important outcome today…'}
                className="w-full px-4 py-3 rounded-xl text-[14px] font-medium outline-none transition-colors"
                style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)', color: 'var(--pd-text)', minHeight: 80 }}
                maxLength={120}
                autoFocus
              />
              <div className="text-[10px] mt-2 text-right" style={{ color: 'var(--pd-subtle)' }}>{focus.length}/120</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderTop: '1px solid var(--pd-border)' }}>
          <button
            data-testid="daybreak-back"
            onClick={goPrev}
            disabled={step === 0}
            className="text-[12px] font-medium transition-opacity"
            style={{ color: 'var(--pd-muted)', opacity: step === 0 ? 0.3 : 1 }}
          >
            Back
          </button>
          <div className="flex items-center gap-2">
            {step < 2 ? (
              <button
                data-testid="daybreak-next"
                onClick={goNext}
                disabled={step === 0 && !canProceedStep0}
                className="px-5 py-2.5 rounded-full text-[12px] font-semibold flex items-center gap-2 transition-opacity"
                style={{ background: 'var(--pd-focus)', color: 'white', opacity: (step === 0 && !canProceedStep0) ? 0.4 : 1 }}
              >
                Continue <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                data-testid="daybreak-save"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 rounded-full text-[12px] font-semibold flex items-center gap-2 transition-opacity"
                style={{ background: 'var(--pd-green)', color: 'white' }}
              >
                {saving ? 'Saving…' : <>Start the day <Check className="w-3.5 h-3.5" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}


/**
 * Soft eyebrow nudge: "🛡 Security: N unrecognised device this week — review".
 *
 * Hidden when count == 0 (no nag if everything's already trusted) and when
 * the standalone anomaly chip is already shown (avoid double-warning the
 * same fact). Clicking "review" closes the Daybreak modal and lands the
 * operator on /settings, where the Recent sign-ins panel lives.
 */
function DaybreakSecurityNudge({ user, onReview }) {
  const count = countUnrecognisedDevices(user?.signinHistory || [], user?.trustedDevices || []);
  if (count <= 0) return null;
  // Don't duplicate the anomaly chip's signal — that one already nags about
  // the current session being odd; this nudge is the periodic catch-up cue.
  if (user?.signinAnomaly) return null;

  const label = `${count} unrecognised device${count === 1 ? '' : 's'} this week`;
  return (
    <button
      data-testid="daybreak-security-nudge"
      onClick={onReview}
      className="mt-2 inline-flex items-center gap-1.5 text-[11px] underline-offset-2 hover:underline transition-colors"
      style={{ color: 'var(--pd-subtle)', letterSpacing: '-0.005em' }}
      title="Open Settings → Recent sign-ins"
    >
      <ShieldAlert className="w-3 h-3" style={{ color: 'var(--pd-warning)' }} />
      <span>
        Security · {label} — <span style={{ color: 'var(--pd-warning)' }}>review</span>
      </span>
    </button>
  );
}

/**
 * StreakChip — flame-icon pill rendered in the Daybreak modal header
 * when the user has a non-zero streak. Three subtle visual states:
 *
 *   `active`  → bright flame, "5 mornings in a row" — celebrates.
 *   `at_risk` → dimmed flame, "Streak of 5 at risk" — gentle prompt.
 *   `broken`  → component doesn't render (caller short-circuits).
 *
 * Designed to be quietly proud, not aggressively gamified — productivity
 * apps that scream "🔥🔥🔥 GREAT JOB!" feel infantilising. The chip
 * states its fact, no more.
 */
function StreakChip({ current, status }) {
  const isActive = status === 'active';
  const isAtRisk = status === 'at_risk';
  const noun = current === 1 ? 'morning' : 'mornings';
  return (
    <span
      data-testid="daybreak-streak-chip"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold normal-case tracking-normal"
      style={{
        background: isActive ? 'rgba(245, 158, 11, 0.12)' : 'rgba(120, 113, 108, 0.10)',
        color: isActive ? 'var(--pd-warning)' : 'var(--pd-subtle)',
        border: '1px solid ' + (isActive ? 'rgba(245, 158, 11, 0.30)' : 'var(--pd-border)'),
        letterSpacing: '-0.01em',
      }}
      title={
        isAtRisk ? `Your ${current}-day streak is at risk — finish today's Daybreak to keep it going.`
        : `${current} consecutive ${noun} of Daybreak — keep going!`
      }
    >
      <Flame
        className="w-3 h-3"
        style={{ color: isActive ? 'var(--pd-warning)' : 'var(--pd-subtle)' }}
      />
      <span>
        {current} {noun}
        {isAtRisk ? ' · at risk' : ''}
      </span>
    </span>
  );
}

